import { useEffect, useRef, useState } from 'react';

const fields = [
  {
    icon: 'ri-scan-2-line',
    title: '지반조사',
    desc: '현장 시추·탐사 데이터 기반 지층 구조 정밀 분석',
    image: 'https://public.readdy.ai/ai/img_res/c8edaad719916197089f43e2c7ba8e77.jpg',
  },
  {
    icon: 'ri-sensor-line',
    title: '스마트 계측',
    desc: 'IoT 센서 기반 실시간 구조물·지반 거동 모니터링',
    image: 'https://readdy.ai/api/search-image?query=smart%20monitoring%20sensors%20installed%20on%20civil%20engineering%20structure%2C%20IoT%20measurement%20devices%20on%20bridge%20or%20retaining%20wall%2C%20data%20collection%20equipment%2C%20professional%20technical%20setup%2C%20clean%20industrial%20environment%2C%20realistic%20photography&width=600&height=400&seq=field-2-geo-v1&orientation=landscape',
  },
  {
    icon: 'ri-shield-check-line',
    title: '정밀 안전진단',
    desc: '공공시설물 안전등급 평가 및 보수보강 방안 제시',
    image: 'https://readdy.ai/api/search-image?query=structural%20safety%20inspection%20engineer%20examining%20concrete%20bridge%20infrastructure%2C%20professional%20safety%20assessment%20work%2C%20inspection%20equipment%20and%20tools%2C%20civil%20engineering%20evaluation%2C%20realistic%20photography%2C%20natural%20lighting&width=600&height=400&seq=field-3-geo-v1&orientation=landscape',
  },
  {
    icon: 'ri-brain-line',
    title: 'AI 기반 지반 데이터 분석',
    desc: '생성형 AI로 복잡한 지반 데이터를 자동 해석·보고',
    image: 'https://readdy.ai/api/search-image?query=data%20analysis%20dashboard%20showing%20geotechnical%20data%20visualization%2C%20AI%20powered%20analytics%20interface%20with%20charts%20and%20graphs%2C%20professional%20engineer%20reviewing%20ground%20data%20on%20computer%20screen%2C%20modern%20office%20environment%2C%20clean%20minimal%20design&width=600&height=400&seq=field-4-geo-v1&orientation=landscape',
  },
];

const ServiceFieldsSection = () => {
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="pt-36 pb-28 bg-white">
      <div className="max-w-7xl mx-auto px-8 md:px-16">
        {/* Header */}
        <div
          className={`mb-16 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="w-10 h-1 bg-[#E8521A] rounded-full" />
            <span className="text-[#E8521A] text-sm font-bold tracking-widest uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
              Service Fields
            </span>
          </div>
          <h2
            className="text-4xl md:text-5xl font-black text-[#1B2A4A]"
            style={{ fontFamily: "'Noto Sans KR', sans-serif", letterSpacing: '-0.03em' }}
          >
            주요 수행 분야
          </h2>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {fields.map((field, index) => (
            <div
              key={field.title}
              onMouseEnter={() => setHovered(index)}
              onMouseLeave={() => setHovered(null)}
              className={`group relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 100 + 200}ms`, height: '340px' }}
            >
              {/* Background image */}
              <img
                src={field.image}
                alt={field.title}
                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />

              {/* Overlay */}
              <div
                className={`absolute inset-0 transition-all duration-500 ${
                  hovered === index
                    ? 'bg-gradient-to-t from-[#1B2A4A]/95 via-[#1B2A4A]/70 to-[#1B2A4A]/30'
                    : 'bg-gradient-to-t from-[#1B2A4A]/85 via-[#1B2A4A]/40 to-transparent'
                }`}
              />

              {/* Content */}
              <div className="absolute inset-0 flex flex-col justify-end p-7">
                {/* Icon */}
                <div
                  className={`w-14 h-14 flex items-center justify-center rounded-xl mb-4 transition-all duration-300 ${
                    hovered === index ? 'bg-[#E8521A]' : 'bg-white/15 backdrop-blur-sm'
                  }`}
                >
                  <i className={`${field.icon} text-white text-2xl`} />
                </div>

                <h3
                  className="text-[18px] font-black text-white mb-2 leading-tight"
                  style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
                >
                  {field.title}
                </h3>
                <p
                  className={`text-sm text-white/75 font-medium leading-relaxed transition-all duration-300 ${
                    hovered === index ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                  }`}
                  style={{ fontFamily: "'Noto Sans KR', sans-serif" }}
                >
                  {field.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServiceFieldsSection;
